import React from 'react';

import noop from '@tinkoff/utils/function/noop';

import {useAppSelector} from 'stores/redux_store';

import FileUpload from 'components/file_upload';
import type Textbox from 'components/textbox/textbox';
import type {FileUpload as FileUploadClass} from 'components/file_upload/file_upload';

import {getEditingPost} from 'selectors/posts';
import {type FileInfo} from '@mattermost/types/files';
import {type ServerError} from '@mattermost/types/errors';
import {type FilePreviewInfo} from 'components/file_preview/file_preview';
import FilePreview from 'components/file_preview';
import {makeGetFilesForPost} from 'mattermost-redux/selectors/entities/files';
import type {GlobalState} from 'types/store';

import styles from './styles.module.css';

type Props = {
    textboxRef: React.RefObject<Textbox>;
    onPostFileDeleted: (id: string) => void;
    onPostFilesAdded: (ids: string[]) => void;
    onUploadServerError: (error: string | ServerError | null) => void;
    onUploadClientError: (error: Error | null) => void;
    postFileIds: string[];
    actionsSlot: React.ReactNode;
    channelId: string;
};

const selectFilesForPost = makeGetFilesForPost();

const useFileUpload = (props: Props) => {
    const editingPost = useAppSelector(getEditingPost);
    const {postFileIds, textboxRef, onPostFileDeleted, onPostFilesAdded, onUploadServerError, onUploadClientError} = props;

    const existingFileInfos = useAppSelector((state: GlobalState) => selectFilesForPost(state, editingPost.fileIds));
    const fileUploadRef = React.useRef<FileUploadClass>(null);

    const [pendingFileIds, setPendingFileIds] = React.useState<string[]>([]);
    const [fileInfos, setFileInfos] = React.useState<FileInfo[]>([]);
    const [uploadsProgressPercent, setUploadsProgressPercent] = React.useState({});

    const getFileUploadTarget = React.useCallback(() => {
        return textboxRef.current?.getInputBox?.() || null;
    }, [textboxRef]);

    const getFileCount = React.useCallback(() => {
        return postFileIds.length;
    }, [postFileIds]);

    const handleUploadStart = React.useCallback((clientIds: string[]) => {
        setPendingFileIds([...clientIds]);
    }, []);

    const handleFileUploadComplete = React.useCallback((fileInfos: FileInfo[], clientIds: string[]) => {
        // remove each finished file from uploads
        setPendingFileIds((pendingFileIds) => pendingFileIds.filter((id) => !clientIds.includes(id)));

        setFileInfos((s) => s.concat(fileInfos));
        onPostFilesAdded(fileInfos.map((i) => i.id));
    }, [onPostFilesAdded]);

    const handleUploadServerError = React.useCallback((err: string | ServerError | null, clientId?: string) => {
        if (clientId) {
            setPendingFileIds((pendingFileIds) => pendingFileIds.filter((id) => id !== clientId));
        }

        onUploadServerError(err);
    }, [onUploadServerError]);

    const handleUploadProgress = React.useCallback((filePreviewInfo: FilePreviewInfo) => {
        setUploadsProgressPercent((s) => ({
            ...s,
            [filePreviewInfo.clientId]: filePreviewInfo,
        }));
    }, []);

    const showFilePreview = existingFileInfos.length > 0 || fileInfos.length > 0 || pendingFileIds.length > 0;

    const removePreview = async (id: string) => {
        // Clear previous errors
        onUploadServerError(null);
        onUploadClientError(null);

        if (pendingFileIds.includes(id)) {
            if (fileUploadRef.current && fileUploadRef.current) {
                fileUploadRef.current.cancelUpload(id);
            }
            setPendingFileIds((s) => s.filter((pId) => pId !== id));
        }

        setFileInfos((s) => s.filter(({id: fId}) => fId !== id));

        onPostFileDeleted(id);
    };

    const resultFileInfos = React.useMemo(
        () => {
            // После сохранения возникает дублирование, так как новые fileInfos
            // добавляются в existingFileInfos
            const resultFileInfosMap = new Map<FileInfo['id'], FileInfo>();
            [...existingFileInfos, ...fileInfos].forEach((info) => {
                if (postFileIds.includes(info.id)) {
                    resultFileInfosMap.set(info.id, info);
                }
            });

            return Array.from(resultFileInfosMap, ([, info]) => info);
        },
        [fileInfos, existingFileInfos, postFileIds],
    );

    return {
        getFileCount,
        getFileUploadTarget,
        fileUploadRef,
        handleUploadStart,
        handleFileUploadComplete,
        handleUploadServerError,
        handleUploadClientError: onUploadClientError,
        handleUploadProgress,
        fileInfos: resultFileInfos,
        pendingFileIds,
        showFilePreview,
        uploadsProgressPercent,
        removePreview,
    };
};

export const EditPostFileUpload: React.VoidFunctionComponent<Props> = (props) => {
    const {actionsSlot, channelId} = props;
    const {
        showFilePreview,
        fileUploadRef,
        getFileCount,
        getFileUploadTarget,
        fileInfos,
        pendingFileIds,
        uploadsProgressPercent,
        handleUploadStart,
        handleFileUploadComplete,
        handleUploadServerError,
        handleUploadClientError,
        handleUploadProgress,
        removePreview,
    } = useFileUpload(props);

    return (
        <React.Fragment>
            <div className='post-body__actions'>
                <FileUpload
                    ref={fileUploadRef}
                    fileCount={getFileCount()}
                    getTarget={getFileUploadTarget}
                    onUploadStart={handleUploadStart}
                    onFileUploadChange={noop}
                    onFileUpload={handleFileUploadComplete}
                    onUploadClientError={handleUploadClientError}
                    onUploadServerError={handleUploadServerError}
                    onUploadProgress={handleUploadProgress}
                    postType='post'
                    postAction='edit'
                    channelId={channelId}
                />
                {actionsSlot}
            </div>
            {showFilePreview ? (
                <div className={styles.wrapper}>
                    <FilePreview
                        fileInfos={fileInfos}
                        uploadsProgressPercent={uploadsProgressPercent}
                        onRemove={removePreview}
                        uploadsInProgress={pendingFileIds}
                    />
                </div>
            ) : null}
        </React.Fragment>
    );
};
